import { ArrowUp, Square } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/v2/button";

export function CaptainInput({
  onSend,
  onStop,
  isStreaming,
}: {
  onSend: (message: string) => void;
  onStop?: () => void;
  isStreaming: boolean;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    autoResize();
  };

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed);
    setValue("");
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.focus();
      }
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-border bg-background-alt p-3">
      <div className="flex items-end gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
        <div className="max-h-[80px] flex-1 cursor-default overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={t("captain.inputPlaceholder")}
            rows={1}
            disabled={isStreaming}
            className="min-h-[24px] w-full cursor-text resize-none overflow-hidden bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:opacity-50"
          />
        </div>
        {isStreaming ? (
          <button
            type="button"
            onClick={onStop}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-destructive/10 px-3 py-1.5 text-sm text-destructive transition-colors hover:bg-destructive/20"
          >
            <Square className="size-3" />
            {t("captain.actions.stop")}
          </button>
        ) : (
          <Button
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleSend}
            disabled={!value.trim()}
            className="shrink-0"
          >
            <ArrowUp className="mr-1 size-3" />
            {t("captain.actions.send")}
          </Button>
        )}
      </div>
    </div>
  );
}
